// Considered: SphereEditorOrganism (full AddSphere dialog) — rejected because ConfigureStep is a
// focused progressive form for required fields + perspectives only; SphereEditor is a full
// tabbed editor with arcs, metaspaces, channels, and status factors.
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useAnalytics } from '@center-technologies/analytics';
import type { Schema, SchemaPerspective, SchemaProperty } from 'types/schema';
import type { Sphere } from 'types/global';
import type { RootState } from '@center-pwa/store/store';
import type { TemplateApproach } from '../../../types/templateApproach';
import CheckboxAtom from '../../atoms/checkbox/Checkbox';
import SphereFieldInputMolecule from '../SphereFieldInput/SphereFieldInputMolecule';
import { extractValuePlaceholderDeps } from '../SphereReference/resolveValuePlaceholders';
import PerspectiveSelector from '../PerspectiveSelector/PerspectiveSelector';
import SphereHeaderRowMolecule from '../SphereHeaderRow/SphereHeaderRowMolecule';
import './AddSphereConfigureStepMolecule.scss';

interface PerspectiveAssignment {
  mainSphereId: string;
  perspectiveProperties: { id: string; system_name: string; name: string };
  assigned: Array<{ sphere: Sphere; edge: { edgeId: string; urgency: number } }>;
}

const isFilled = (v: any): boolean =>
  v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== '');

export interface AddSphereConfigureStepMoleculeProps {
  parentSphere: Sphere;
  schema: Schema;
  onBack: () => void;
  onSphereCreated: (sphere: Sphere) => void;
  onClose: () => void;
  /** When set, this approach system_name is included in the POST /spheres payload. */
  selectedApproach?: TemplateApproach;
  /**
   * Offers "Don't know yet" on the Purpose (`center_parent`) row, which creates the sphere
   * unplaced. Defaults to true; pass false where a Purpose parent must be chosen.
   */
  allowUnplaced?: boolean;
}

type FieldValues = Record<string, any>;

const PURPOSE_PARENT = 'center_parent';

const AddSphereConfigureStepMolecule: React.FC<AddSphereConfigureStepMoleculeProps> = ({
  parentSphere,
  schema,
  onBack,
  onSphereCreated,
  onClose,
  selectedApproach,
  allowUnplaced = true,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const logAnalyticsEvent = useAnalytics();
  const userSphere = useSelector((state: RootState) => state.userProfile.userSphere);
  const [values, setValues] = useState<FieldValues>({});
  const [assignedPerspectives, setAssignedPerspectives] = useState<PerspectiveAssignment[]>([]);
  const [initializedSphere, setInitializedSphere] = useState<Sphere | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOptional, setShowOptional] = useState(false);
  const [unplaced, setUnplaced] = useState(false);

  // Org id used to scope reference-field searches (make/model, etc.). A sphere's
  // `sphereId` property carries its org-root id — EXCEPT the org root itself, whose
  // `sphereId` points to its container (the metasphere). So when the parent IS the
  // org (`center_organization`), use its own id; otherwise inherit `sphereId`.
  // Without this, adding a child directly under the org scopes the search to the
  // metasphere and reference dropdowns come back empty.
  const organizationId =
    (parentSphere.properties as any)?.type === 'center_organization'
      ? parentSphere.id
      : ((parentSphere.properties as any)?.sphereId ?? '');

  // Initialize the sphere SERVER-SIDE on load (same canonical path as the inline capture and
  // every other create): /spheres/init runs initializeSphere with the parent's graph data,
  // so center/parent/responsible perspectives are auto-resolved and schema property defaults
  // are filled. We seed the form from the result; the user edits from there.
  useEffect(() => {
    let cancelled = false;
    const graphClient = apiClients?.graphClient;
    if (!graphClient) return;
    (async () => {
      try {
        const resp = await graphClient.post<{ sphere?: Sphere }>('/spheres/init', {
          schemaSystemName: schema.system_name,
          centerSphereId: parentSphere.id,
          userSphereId: userSphere?.id,
        });
        if (cancelled || !resp?.sphere) return;
        setInitializedSphere(resp.sphere);
        const initProps = (resp.sphere.properties as Record<string, any>) ?? {};
        // Seed defaults but never clobber edits the user already made.
        setValues((prev) => ({ ...initProps, ...prev }));
        const initAssigned = (resp.sphere as any).assignedPerspectives;
        if (Array.isArray(initAssigned) && initAssigned.length > 0) {
          setAssignedPerspectives(initAssigned as PerspectiveAssignment[]);
        }
      } catch {
        /* init is best-effort — the form still works with manual entry */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClients, schema.system_name, parentSphere.id, userSphere?.id]);

  // Synthetic in-progress sphere passed to PerspectiveSelector so it reflects the
  // current selections (it reads sphere.assignedPerspectives).
  const draftSphereForPicker = useMemo(
    () =>
      ({
        id: '',
        label: schema.node_label,
        properties: { sphereId: organizationId, type: schema.system_name },
        assignedPerspectives,
      }) as unknown as Sphere,
    [organizationId, schema.node_label, schema.system_name, assignedPerspectives],
  );

  const upsertAssignment = useCallback(
    (systemName: string, displayName: string, spheres: Sphere[]) => {
      setAssignedPerspectives((prev) => {
        const others = prev.filter((ap) => ap.perspectiveProperties.system_name !== systemName);
        if (!spheres || spheres.length === 0) return others;
        return [
          ...others,
          {
            mainSphereId: '',
            perspectiveProperties: { id: '', system_name: systemName, name: displayName },
            assigned: spheres.map((s) => ({ sphere: s, edge: { edgeId: '', urgency: 3 } })),
          },
        ];
      });
    },
    [],
  );

  // System properties with dedicated UI elsewhere (or not user-editable on create) — kept
  // out of the auto field lists, mirroring AdditionalPropertiesGrid's exclusions. `urgency`
  // is excluded too: like Sphere Editor it's surfaced up by the name field, not in optional.
  const isFieldProp = (p: SchemaProperty) =>
    !p.read_only && !p.hidden && p.name !== 'is_private' && p.name !== 'type' && p.name !== 'urgency';

  // Urgency renders inline in the name row (consistency with Sphere Editor's header).
  const urgencyProp: SchemaProperty | undefined = useMemo(
    () => schema.properties.find((p) => p.name === 'urgency' && !p.read_only && !p.hidden),
    [schema.properties],
  );

  // Current urgency level (1–5); defaults to medium (3) like Sphere Editor.
  const urgencyValue = useMemo(() => {
    const raw = values['urgency'] ?? (urgencyProp as any)?.default_value;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 1 && n <= 5 ? n : 3;
  }, [values, urgencyProp]);

  // Privacy lock renders inline in the name row when the schema exposes is_private.
  const privacyProp: SchemaProperty | undefined = useMemo(
    () => schema.properties.find((p) => p.name === 'is_private' && !p.read_only && !p.hidden),
    [schema.properties],
  );

  // Sphere-shaped object feeding the shared header's type avatar (glyph + color).
  const headerSphere = useMemo(
    () =>
      ({
        id: initializedSphere?.id ?? '',
        label: schema.node_label,
        glyph: (initializedSphere as any)?.glyph ?? schema.glyph,
        glyphColor: (initializedSphere as any)?.glyphColor ?? schema.glyph_color,
        properties: { sphereId: organizationId, type: schema.system_name },
      }) as unknown as Sphere,
    [initializedSphere, schema.node_label, schema.glyph, schema.glyph_color, schema.system_name, organizationId],
  );

  const requiredProps: SchemaProperty[] = useMemo(
    () => schema.properties.filter((p) => p.required && isFieldProp(p)),
    [schema.properties],
  );

  // Required fields shown in the grid below the name row (name lives in the name row).
  const requiredGridProps: SchemaProperty[] = useMemo(
    () => requiredProps.filter((p) => p.name !== 'name'),
    [requiredProps],
  );

  const optionalProps: SchemaProperty[] = useMemo(
    () => schema.properties.filter((p) => !p.required && isFieldProp(p)),
    [schema.properties],
  );

  const requiredPerspectives: SchemaPerspective[] = useMemo(
    () => schema.perspectives.filter((p) => p.required),
    [schema.perspectives],
  );

  // Perspectives to render: the core structural ones (center, parent, responsible) are
  // always shown when the schema defines them — even if not strictly required — plus any
  // other required perspective. Structural ones lead, in a stable order. Completion is
  // still gated on `requiredPerspectives` only, so optional structural ones don't block.
  const displayedPerspectives: SchemaPerspective[] = useMemo(() => {
    const seen = new Set<string>();
    const ordered: SchemaPerspective[] = [];
    ['center_center', 'center_parent', 'center_responsible'].forEach((sn) => {
      const p = schema.perspectives.find((x) => x.system_name === sn && !x.is_hidden);
      if (p && !seen.has(sn)) {
        ordered.push(p);
        seen.add(sn);
      }
    });
    schema.perspectives.forEach((p) => {
      if (p.required && !p.is_hidden && !seen.has(p.system_name)) {
        ordered.push(p);
        seen.add(p.system_name);
      }
    });
    return ordered;
  }, [schema.perspectives]);

  const fieldsDone = useMemo(
    () => requiredProps.filter((p) => isFilled(values[p.name])).length,
    [requiredProps, values],
  );

  const perspDone = useMemo(
    () =>
      requiredPerspectives.filter(
        (pp) =>
          (unplaced && pp.system_name === PURPOSE_PARENT) ||
          assignedPerspectives.some(
            (ap) => ap.perspectiveProperties.system_name === pp.system_name && ap.assigned.length > 0,
          ),
      ).length,
    [requiredPerspectives, assignedPerspectives, unplaced],
  );

  const totalRequired = requiredProps.length + requiredPerspectives.length;
  const doneCount = fieldsDone + perspDone;
  const isComplete = doneCount >= totalRequired && totalRequired > 0;

  // The Purpose and Center rows are named for what they mean, not for the schema's raw labels.
  const perspectiveLabel = (pp: SchemaPerspective): string => {
    if (pp.system_name === PURPOSE_PARENT) return t('addSphere.dialog.perspectives.purposeLabel');
    if (pp.system_name === 'center_center') return t('addSphere.dialog.perspectives.livesInLabel');
    return pp.label ?? pp.name;
  };

  const toggleUnplaced = useCallback(
    (checked: boolean) => {
      setUnplaced(checked);
      if (checked) upsertAssignment(PURPOSE_PARENT, '', []);
    },
    [upsertAssignment],
  );

  const setVal = useCallback((key: string, val: any) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  }, []);

  // Map of dependent-reference-field → sibling property names it depends on via $value()
  // (mirrors AdditionalPropertiesGrid so cascading references behave the same on create).
  const refDependencies = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const prop of [...requiredGridProps, ...optionalProps]) {
      if (prop.type !== 'reference') continue;
      const deps = extractValuePlaceholderDeps((prop as any).metadata?.targetFilter);
      if (deps.length > 0) map.set(prop.name, deps);
    }
    return map;
  }, [requiredGridProps, optionalProps]);

  // Cascade-clear: when a sibling that a reference field depends on changes,
  // clear the dependent field so a stale selection isn't submitted.
  const prevValuesRef = useRef<Record<string, any> | null>(null);
  useEffect(() => {
    if (prevValuesRef.current === null) {
      prevValuesRef.current = { ...values };
      return;
    }
    const prev = prevValuesRef.current;
    for (const [fieldName, deps] of refDependencies.entries()) {
      const siblingChanged = deps.some((dep) => prev[dep] !== values[dep]);
      if (siblingChanged && values[fieldName] != null) {
        setVal(fieldName, null);
      }
    }
    prevValuesRef.current = { ...values };
  }, [values, refDependencies, setVal]);

  const buildSpherePayload = useCallback(
    (draft: boolean) => {
      // Base on the server-initialized sphere (resolved center/parent/responsible + schema
      // defaults), then layer the form's edits on top.
      const baseProps = (initializedSphere?.properties as Record<string, any>) ?? {};
      const properties: Record<string, any> = { ...baseProps };
      schema.properties.forEach((p) => {
        if (values[p.name] !== undefined) properties[p.name] = values[p.name];
      });
      properties['name'] = values['name'] ?? properties['name'] ?? '';

      // An unplaced sphere carries no Purpose parent; the server rejects both together.
      const sentPerspectives = unplaced
        ? assignedPerspectives.filter((ap) => ap.perspectiveProperties.system_name !== PURPOSE_PARENT)
        : assignedPerspectives;

      const sphere: Record<string, unknown> = initializedSphere
        ? { ...initializedSphere, properties, assignedPerspectives: sentPerspectives }
        : { name: properties['name'], centerId: parentSphere.id, properties, assignedPerspectives: sentPerspectives };

      const payload: Record<string, unknown> = {
        schemaSystemName: schema.system_name,
        sphere,
      };

      if (draft) {
        payload.draft = true;
      }

      if (unplaced) {
        payload.unplaced = true;
      }

      // Carry selected template approach into the create payload so it deploys on create.
      if (selectedApproach) {
        payload.approaches = [selectedApproach.system_name];
      }

      return payload;
    },
    [initializedSphere, schema, values, parentSphere, assignedPerspectives, selectedApproach, unplaced],
  );

  const handleSaveAsDraft = useCallback(async () => {
    if (!apiClients?.graphClient || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const resp = await apiClients.graphClient.post<{ status: string; sphere?: Sphere }>(
        '/spheres',
        buildSpherePayload(true),
      );
      if (resp.sphere) {
        logAnalyticsEvent('draft_sphere_captured', undefined, {
          sphereId: resp.sphere.id,
          schemaSystemName: schema.system_name,
          parentSphereId: parentSphere.id,
          source: 'dialog',
        });
        onSphereCreated(resp.sphere);
        onClose();
      } else {
        setError(t('addSphere.dialog.errors.saveDraftFailed'));
      }
    } catch (err) {
      logAnalyticsEvent('sphere_create_failed', undefined, {
        error: String(err),
        schemaSystemName: schema.system_name,
        parentSphereId: parentSphere.id,
        draft: true,
      });
      setError(t('addSphere.dialog.errors.saveDraftFailed'));
    } finally {
      setIsSubmitting(false);
    }
  }, [apiClients, isSubmitting, buildSpherePayload, schema, parentSphere, logAnalyticsEvent, onSphereCreated, onClose, t]);

  const handleCreateSphere = useCallback(async () => {
    if (!apiClients?.graphClient || isSubmitting || !isComplete) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const resp = await apiClients.graphClient.post<{ status: string; sphere?: Sphere }>(
        '/spheres',
        buildSpherePayload(false),
      );
      if (resp.sphere) {
        logAnalyticsEvent('sphere_created', undefined, {
          sphereId: resp.sphere.id,
          schemaSystemName: schema.system_name,
          parentSphereId: parentSphere.id,
        });
        if (unplaced) {
          logAnalyticsEvent('sphere_created_unplaced', undefined, { schemaSystemName: schema.system_name });
        }
        onSphereCreated(resp.sphere);
        onClose();
      } else {
        setError(t('addSphere.dialog.errors.createFailed'));
      }
    } catch (err) {
      logAnalyticsEvent('sphere_create_failed', undefined, {
        error: String(err),
        schemaSystemName: schema.system_name,
        parentSphereId: parentSphere.id,
        draft: false,
      });
      setError(t('addSphere.dialog.errors.createFailed'));
    } finally {
      setIsSubmitting(false);
    }
  }, [apiClients, isSubmitting, isComplete, buildSpherePayload, schema, parentSphere, unplaced, logAnalyticsEvent, onSphereCreated, onClose, t]);

  return (
    <div className="add-sphere-configure-step">
      <div className="add-sphere-configure-step__body">
        <div className="add-sphere-configure-step__form">
          {/* Name row: same single-pill header as the Sphere Editor (type avatar + caret,
              urgency, privacy, name). Clicking the type avatar returns to type selection. */}
          <div className="add-sphere-configure-step__name-row">
            <SphereHeaderRowMolecule
              sphere={headerSphere}
              typeTitle={t('addSphere.dialog.changeType', { type: schema.name })}
              onTypeClick={onBack}
              name={values['name'] ?? ''}
              onNameChange={(v) => setVal('name', v)}
              namePlaceholder={t('addSphere.dialog.fieldPlaceholder', { field: 'name' })}
              nameInvalid={!isFilled(values['name'])}
              showUrgency={!!urgencyProp}
              urgencyValue={urgencyValue}
              onUrgencyChange={(u) => setVal('urgency', u)}
              showPrivacy={!!privacyProp}
              isPrivate={!!values['is_private']}
              onPrivacyToggle={() => setVal('is_private', !values['is_private'])}
              hasManageSecurityPermission
            />
          </div>

          {/* Required fields (name is surfaced above in the name row) */}
          {requiredGridProps.length > 0 && (
            <section className="add-sphere-configure-step__section">
              <div className="add-sphere-configure-step__section-header">
                <span className="add-sphere-configure-step__section-title">
                  {t('addSphere.dialog.sections.requiredFields')}
                </span>
                <span
                  className={`add-sphere-configure-step__progress-badge${fieldsDone === requiredProps.length ? ' add-sphere-configure-step__progress-badge--done' : ''}`}
                >
                  {t('addSphere.dialog.progress.fieldsComplete', {
                    done: fieldsDone,
                    total: requiredProps.length,
                  })}
                </span>
              </div>
              <div className="add-sphere-configure-step__progress-bar">
                <div
                  className={`add-sphere-configure-step__progress-fill${fieldsDone === requiredProps.length ? ' add-sphere-configure-step__progress-fill--done' : ''}`}
                  style={{
                    width: `${requiredProps.length > 0 ? (fieldsDone / requiredProps.length) * 100 : 0}%`,
                  }}
                />
              </div>
              <div className="add-sphere-configure-step__fields-grid">
                {requiredGridProps.map((p) => (
                    <div
                      key={p.name}
                      className={`add-sphere-configure-step__field${p.type === 'text' || p.name === 'description' ? ' add-sphere-configure-step__field--full' : ''}`}
                    >
                      <label className="add-sphere-configure-step__field-label" htmlFor={p.name}>
                        {p.label || p.name} <span className="add-sphere-configure-step__required-star">*</span>
                      </label>
                      <SphereFieldInputMolecule
                        property={p}
                        value={values[p.name]}
                        onChange={(v) => setVal(p.name, v)}
                        invalid={!isFilled(values[p.name])}
                        placeholder={t('addSphere.dialog.fieldPlaceholder', { field: p.name.toLowerCase() })}
                        siblingValues={values}
                        organizationId={organizationId}
                      />
                      {p.description && (
                        <span className="add-sphere-configure-step__field-help">{p.description}</span>
                      )}
                    </div>
                  ))}
              </div>
            </section>
          )}

          {/* Perspectives (structural ones always shown; completion gated on required) */}
          {displayedPerspectives.length > 0 && (
            <section className="add-sphere-configure-step__section">
              <div className="add-sphere-configure-step__section-header">
                <span className="add-sphere-configure-step__section-title">
                  {t('addSphere.dialog.sections.requiredPerspectives')}
                </span>
                {requiredPerspectives.length > 0 && (
                  <span
                    className={`add-sphere-configure-step__progress-badge${perspDone === requiredPerspectives.length ? ' add-sphere-configure-step__progress-badge--done' : ''}`}
                  >
                    {t('addSphere.dialog.progress.perspectivesAssigned', {
                      done: perspDone,
                      total: requiredPerspectives.length,
                    })}
                  </span>
                )}
              </div>
              <p className="add-sphere-configure-step__perspectives-desc">
                {t('addSphere.dialog.perspectives.description', { schemaName: schema.name })}
              </p>
              <div className="add-sphere-configure-step__fields-grid">
                {displayedPerspectives.map((pp) => (
                  <div key={pp.system_name} className="add-sphere-configure-step__field">
                    <label className="add-sphere-configure-step__field-label">
                      {perspectiveLabel(pp)}{' '}
                      {pp.system_name === 'center_center' ? (
                        <span className="add-sphere-configure-step__mandatory-badge">
                          {t('addSphere.dialog.perspectives.mandatory')}
                        </span>
                      ) : pp.required ? (
                        <span className="add-sphere-configure-step__required-star">*</span>
                      ) : (
                        <span className="add-sphere-configure-step__optional-tag">
                          {t('addSphere.dialog.perspectives.optional')}
                        </span>
                      )}
                    </label>
                    {pp.system_name === PURPOSE_PARENT && allowUnplaced && (
                      <div className="add-sphere-configure-step__unplaced">
                        <CheckboxAtom
                          className="add-sphere-configure-step__unplaced-option"
                          label={t('addSphere.dialog.perspectives.unplacedOption')}
                          checked={unplaced}
                          onChange={toggleUnplaced}
                        />
                        {unplaced && (
                          <span className="add-sphere-configure-step__field-help">
                            {t('addSphere.dialog.perspectives.unplacedHint')}
                          </span>
                        )}
                      </div>
                    )}
                    {!(unplaced && pp.system_name === PURPOSE_PARENT) && (
                    <PerspectiveSelector
                      perspectiveSystemName={pp.system_name}
                      sphere={draftSphereForPicker}
                      organizationId={organizationId}
                      multi={pp.cardinality === 'multiple'}
                      isEditing
                      // Single-select perspectives must stay open, otherwise the dropdown
                      // closes on the first outside click and (with nothing assigned yet)
                      // the field renders empty. Mirrors SphereEditor's PerspectiveAssignmentsPanel.
                      stayOpen={pp.cardinality !== 'multiple'}
                      showLabel={false}
                      label={pp.label ?? pp.name}
                      onAssigneeSelected={(_id, _sid, psn, assigneeSphere) =>
                        upsertAssignment(psn, pp.label ?? pp.name, assigneeSphere ? [assigneeSphere] : [])
                      }
                      onAssigneesSelected={(_ids, _sid, psn, assigneeSpheres) =>
                        upsertAssignment(psn, pp.label ?? pp.name, assigneeSpheres ?? [])
                      }
                    />
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Optional fields */}
          {optionalProps.length > 0 && (
            <section className="add-sphere-configure-step__section">
              <button
                className="add-sphere-configure-step__optional-toggle"
                onClick={() => setShowOptional((v) => !v)}
                type="button"
              >
                <i className={`pi ${showOptional ? 'pi-chevron-down' : 'pi-chevron-right'}`} />
                <span className="add-sphere-configure-step__section-title">
                  {t('addSphere.dialog.sections.optionalFields')}
                </span>
                <span className="add-sphere-configure-step__optional-count">
                  {t('addSphere.dialog.optional.count', { count: optionalProps.length })}
                </span>
              </button>
              {showOptional && (
                <div className="add-sphere-configure-step__fields-grid add-sphere-configure-step__fields-grid--optional">
                  {optionalProps.map((p) => (
                    <div
                      key={p.name}
                      className={`add-sphere-configure-step__field${p.type === 'text' || p.name === 'urgency' || p.name === 'description' ? ' add-sphere-configure-step__field--full' : ''}`}
                    >
                      <label className="add-sphere-configure-step__field-label" htmlFor={p.name}>{p.label || p.name}</label>
                      <SphereFieldInputMolecule
                        property={p}
                        value={values[p.name]}
                        onChange={(v) => setVal(p.name, v)}
                        placeholder={p.description ?? p.name}
                        siblingValues={values}
                        organizationId={organizationId}
                      />
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>

        {/* Right rail */}
        <aside className="add-sphere-configure-step__rail">
          <div className="add-sphere-configure-step__rail-section-label">
            {t('addSphere.dialog.sections.youllGet')}
          </div>
          {schema.properties.length > 0 && (
            <div className="add-sphere-configure-step__provision-item">
              <i className="pi pi-list" />
              <span className="add-sphere-configure-step__provision-label">
                {t('addSphere.dialog.provision.properties')}
              </span>
              <span className="add-sphere-configure-step__provision-count">{schema.properties.length}</span>
            </div>
          )}
          {schema.perspectives.length > 0 && (
            <div className="add-sphere-configure-step__provision-item">
              <i className="pi pi-users" />
              <span className="add-sphere-configure-step__provision-label">
                {t('addSphere.dialog.provision.perspectives')}
              </span>
              <span className="add-sphere-configure-step__provision-count">{schema.perspectives.length}</span>
            </div>
          )}
        </aside>
      </div>

      {/* Footer */}
      <footer className="add-sphere-configure-step__footer">
        <div className="add-sphere-configure-step__footer-status">
          <i className={`pi ${isComplete ? 'pi-check-circle' : 'pi-exclamation-circle'}`} />
          {isComplete
            ? t('addSphere.dialog.footer.allComplete')
            : t('addSphere.dialog.footer.fieldsRemaining', {
                count: totalRequired - doneCount,
              })}
        </div>

        {error && (
          <span className="add-sphere-configure-step__footer-error" role="alert">
            {error}
          </span>
        )}

        <div className="add-sphere-configure-step__footer-actions">
          {!isComplete && (
            <button
              type="button"
              className="add-sphere-configure-step__save-draft-btn"
              onClick={handleSaveAsDraft}
              disabled={isSubmitting || !values['name']?.trim()}
            >
              <i className="pi pi-save" />
              {t('addSphere.dialog.footer.saveAsDraft')}
            </button>
          )}
          <button
            type="button"
            className="add-sphere-configure-step__create-btn"
            onClick={handleCreateSphere}
            disabled={!isComplete || isSubmitting}
          >
            <i className="pi pi-plus" />
            {t('addSphere.dialog.footer.createSphere')}
          </button>
        </div>
      </footer>
    </div>
  );
};

export default AddSphereConfigureStepMolecule;
